import React, { useEffect, useState } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Loading from 'src/components/common/Loading';
import { v2SessionInfo, v2GetDivisions } from 'src/store/modules/auth/v2Operations';
import { getAppUrlForReferrer, getReferrerInfo, type AppStage } from 'src/utils/ssoReferrer';

const VALID_STAGES: ReadonlySet<AppStage> = new Set<AppStage>(['prod', 'dev', 'qa', 'localhost']);

/**
 * Read the originating app's stage from the `?stage=` query param if present.
 *
 * The SSO host is shared between dev and prod (`signin.stackline.com`), so
 * `getReferrerInfo().stage` (which derives stage from the current hostname)
 * always returns `prod` here for both dev and prod traffic. Apps redirecting
 * to `/no-access` now forward their actual page stage via this param so we
 * can route the user back to the matching stage after picking an accessible
 * app. Prod traffic omits the param entirely (backward compat).
 *
 * Returns null when the param is missing, empty, or not a recognized stage.
 */
function readStageOverride(params: URLSearchParams): AppStage | null {
  const raw = params.get('stage')?.trim().toLowerCase();
  if (!raw) return null;
  return VALID_STAGES.has(raw as AppStage) ? (raw as AppStage) : null;
}

const APP_REDIRECT_PRIORITY = ['atlas', 'beacon', 'advisor', 'admanager'];

const CenteredContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  gap: '16px'
});

const NoAccessRedirect: React.FC<RouteComponentProps> = ({ history }) => {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const resolve = async () => {
      const params = new URLSearchParams(window.location.search);
      const deniedApp = (params.get('app') || 'atlas').toLowerCase();
      const divisionName = params.get('divName') || 'your division';

      try {
        // Need both: liveness (to know if we should sign-in instead) and
        // the active division's appAccess map (to pick the redirect target).
        // Replaces the previous single /v2/validate call.
        const [sessionInfo, divisionsResp] = await Promise.all([
          v2SessionInfo(),
          v2GetDivisions().catch(() => null),
        ]);

        if (!sessionInfo.authenticated) {
          history.push('/sign_in');
          return;
        }

        const activeDivision = divisionsResp?.divisions?.find(
          d => d.id === sessionInfo.divisionId,
        );
        const appAccess = activeDivision?.appAccess;
        const accessibleApps = appAccess
          ? Object.entries(appAccess)
              .filter(([name, level]) => name !== deniedApp && level && level !== 'blocked')
              .map(([name]) => name)
          : [];

        const targetApp =
          APP_REDIRECT_PRIORITY.find((a) => accessibleApps.includes(a)) || accessibleApps[0];

        if (targetApp) {
          // Prefer the originating page's stage when the redirecting app
          // forwarded it via `?stage=` (admanager + future apps). Falls back
          // to the SSO host's own stage so legacy callers — and prod traffic
          // (which intentionally omits the param) — keep working unchanged.
          const stageOverride = readStageOverride(params);
          const stage = stageOverride ?? getReferrerInfo().stage;
          const referrer = stage === 'prod' ? targetApp : `${targetApp}-${stage}`;
          const targetBaseUrl = getAppUrlForReferrer(referrer);
          const sep = targetBaseUrl.includes('?') ? '&' : '?';
          const redirectParams = `sl_redirect=1&sl_from=${encodeURIComponent(deniedApp)}&sl_div=${encodeURIComponent(divisionName)}`;
          window.location.href = `${targetBaseUrl}${sep}${redirectParams}`;
        } else {
          window.location.href = `/switch-division?app=${encodeURIComponent(deniedApp)}`;
        }
      } catch (err: any) {
        if (err.response?.status === 401) {
          history.push('/sign_in');
        } else {
          setError('Something went wrong. Redirecting...');
          setTimeout(() => {
            window.location.href = `/switch-division?app=${encodeURIComponent(deniedApp)}`;
          }, 1500);
        }
      }
    };

    resolve();
  }, [history]);

  return (
    <CenteredContainer>
      <Loading />
      <Typography variant="body1" color="text.secondary">
        {error || 'Checking access...'}
      </Typography>
    </CenteredContainer>
  );
};

export default withRouter(NoAccessRedirect);
